import type { ActionType, ILotModelConfig } from 'utils/servicePointsCatalog'

import { resolveSelectedCatalogActions } from './catalogItemActions'
import {
  getCatalogServiceActionLabel,
  isCatalogServiceRowActive,
  supportsCatalogServiceAction,
} from './serviceCalibLotService'
import type { ServiceCalibCatalogItem } from './serviceCalibTypes'
import { getCalibrateButtonTitle, getCatalogServiceButtonTitle, getFinishCalibButtonTitle } from './serviceCalibUtils'

export type CatalogPrimaryActionKind = 'hidden' | 'stop' | 'finish' | 'calibrate' | 'choose-actions' | 'service'

export type CatalogPrimaryActionState = {
  kind: CatalogPrimaryActionKind
  serviceLabel: string | null
  title: string
  loading: boolean
  disabled: boolean
}

type ResolveCatalogPrimaryActionParams = {
  item: ServiceCalibCatalogItem
  calibrated: boolean
  showFinishCalib: boolean
  calibFailed: boolean
  needsActionSelection: boolean
  lotModels: ILotModelConfig[]
  selectedActionsByKey: Record<string, ActionType[]>
  robotType?: string | null
  isOnline: boolean
  lidarLocalizedForCalib: boolean
  modeInProgress: boolean
  blockedByOtherCatalogOperation: boolean
  commandsLockedEffective: boolean
  isCatalogServiceModeAvailable: boolean
  serviceUiActive: boolean
  confirmedServiceKey: string | null
  calibLoading: boolean
  calibFinishLoading: boolean
  serviceStartLoading: boolean
  serviceStopLoading: boolean
  serviceRowActivating: boolean
  noConfirmation?: boolean
}

export function resolveCatalogPrimaryAction(params: ResolveCatalogPrimaryActionParams): CatalogPrimaryActionState {
  const {
    item,
    calibrated,
    showFinishCalib,
    calibFailed,
    needsActionSelection,
    selectedActionsByKey,
    lotModels,
    robotType,
    isOnline,
    lidarLocalizedForCalib,
    modeInProgress,
    blockedByOtherCatalogOperation,
    commandsLockedEffective,
    isCatalogServiceModeAvailable,
    serviceUiActive,
    confirmedServiceKey,
    calibLoading,
    calibFinishLoading,
    serviceStartLoading,
    serviceStopLoading,
    serviceRowActivating,
    noConfirmation,
  } = params

  const selectedActions = resolveSelectedCatalogActions(item, selectedActionsByKey, lotModels, robotType)
  const canStartService = supportsCatalogServiceAction(item) && selectedActions.length > 0
  const serviceLabel = getCatalogServiceActionLabel(item, false, selectedActions)
  const activeOnRow = isCatalogServiceRowActive(item, confirmedServiceKey)
  const serviceRowLoading = serviceStartLoading || serviceStopLoading || serviceRowActivating
  const unavailable = !activeOnRow && !isCatalogServiceModeAvailable
  const blockedByOtherService = serviceUiActive && !serviceRowLoading && !activeOnRow

  const noConfirmationTitle = noConfirmation ? 'No confirmation' : null

  const rowHintBase = {
    isOnline,
    ready: calibrated,
    modeInProgress,
    blockedByOtherOperation: blockedByOtherCatalogOperation,
  }

  if (activeOnRow && supportsCatalogServiceAction(item)) {
    return {
      kind: 'stop',
      serviceLabel,
      title:
        noConfirmationTitle ??
        getCatalogServiceButtonTitle({
          ...rowHintBase,
          loading: serviceRowLoading,
          activeOnRow: true,
          actionLabel: serviceLabel ?? 'Stop',
        }),
      loading: serviceRowLoading,
      disabled: serviceRowLoading || !isOnline,
    }
  }

  if (showFinishCalib) {
    return {
      kind: 'finish',
      serviceLabel,
      title:
        noConfirmationTitle ??
        getFinishCalibButtonTitle({
        isOnline,
        ready: calibrated,
        modeInProgress: false,
        blockedByOtherOperation: false,
        loading: calibFinishLoading,
        calibFailed,
      }),
      loading: calibFinishLoading,
      disabled: calibFinishLoading || !isOnline,
    }
  }

  if (!calibrated) {
    return {
      kind: 'calibrate',
      serviceLabel,
      title:
        noConfirmationTitle ??
        getCalibrateButtonTitle({
        ...rowHintBase,
        loading: calibLoading,
        lidarLocalized: lidarLocalizedForCalib,
        calibFailed,
      }),
      loading: calibLoading,
      disabled:
        calibLoading ||
        !lidarLocalizedForCalib ||
        !isOnline ||
        (commandsLockedEffective && !calibLoading) ||
        modeInProgress ||
        blockedByOtherCatalogOperation,
    }
  }

  if (needsActionSelection) {
    return {
      kind: 'choose-actions',
      serviceLabel,
      title:
        noConfirmationTitle ??
        getCatalogServiceButtonTitle({
          ...rowHintBase,
          loading: serviceRowLoading,
          activeOnRow: false,
          actionLabel: serviceLabel ?? 'Choose actions',
          needsActionSelection: true,
        }),
      loading: serviceRowLoading,
      disabled: true,
    }
  }

  if (!canStartService || !serviceLabel) {
    return {
      kind: 'hidden',
      serviceLabel: null,
      title: '',
      loading: false,
      disabled: true,
    }
  }

  return {
    kind: 'service',
    serviceLabel,
    title:
      noConfirmationTitle ??
      getCatalogServiceButtonTitle({
        ...rowHintBase,
        loading: serviceRowLoading,
        activeOnRow: false,
        actionLabel: serviceLabel,
        serviceModeUnavailable: unavailable,
      }),
    loading: serviceRowLoading,
    disabled:
      serviceRowLoading ||
      !isOnline ||
      (commandsLockedEffective && !serviceRowLoading) ||
      modeInProgress ||
      blockedByOtherCatalogOperation ||
      blockedByOtherService ||
      unavailable ||
      !calibrated,
  }
}
